import { useForm } from "@inertiajs/react";
import { Activity, Settings } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { admin_chaos } from "@/routes.js";
import type { ChaosConfig } from "@/types";

interface DashboardProps {
	chaos: ChaosConfig;
	flash?: {
		success?: string;
		error?: string;
	};
}

export default function Dashboard({ chaos, flash }: DashboardProps) {
	const { data, setData, patch, processing, recentlySuccessful } = useForm({
		chaos,
	});

	const { enabled, latency_ms, failure_rate, failure_type } = data.chaos;

	useEffect(() => {
		if (flash?.success) {
			toast.success(flash.success);
		}
		if (flash?.error) {
			toast.error(flash.error);
		}
	}, [flash]);

	const handleSave = () => {
		patch(admin_chaos(), {
			preserveState: true,
			preserveUrl: true,
		});
	};

	const formatFailureType = (type: string) => {
		return type
			.split("_")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(" ");
	};

	return (
		<div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 min-h-full">
			<div className="container mx-auto py-8 px-4 max-w-6xl">
				{/* Header */}
				<div className="mb-8">
					<div className="flex items-center gap-3 mb-2">
						<Settings className="h-8 w-8 text-primary" />
						<h1 className="text-4xl font-bold">Admin Dashboard</h1>
					</div>
					<p className="text-muted-foreground text-lg">
						Manage system configuration and chaos engineering settings
					</p>
				</div>

				{/* Flash Messages */}
				{flash?.success && (
					<Alert className="mb-6 border-green-500 bg-green-50 dark:bg-green-950">
						<AlertDescription className="text-green-900 dark:text-green-100">
							{flash.success}
						</AlertDescription>
					</Alert>
				)}

				{flash?.error && (
					<Alert variant="destructive" className="mb-6">
						<AlertDescription>{flash.error}</AlertDescription>
					</Alert>
				)}

				{/* Main Content Grid */}
				<div className="grid gap-6">
					{/* Chaos Engineering Section */}
					<Card className="border-2">
						<CardHeader className="space-y-1">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-3">
									<Activity className="h-5 w-5 text-orange-500" />
									<CardTitle className="text-2xl">Chaos Engineering</CardTitle>
								</div>
								<Badge
									variant={enabled ? "destructive" : "secondary"}
									className="text-sm px-3 py-1"
								>
									{enabled ? "Active" : "Inactive"}
								</Badge>
							</div>
							<CardDescription className="text-base">
								Test system resilience by injecting controlled failures and
								latency into API requests
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-6">
							{/* Enable/Disable Toggle */}
							<div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
								<div className="space-y-0.5">
									<Label htmlFor="enabled" className="text-base font-medium">
										Enable Chaos Engineering
									</Label>
									<p className="text-sm text-muted-foreground">
										Activate failure injection and latency controls
									</p>
								</div>
								{/** biome-ignore lint/correctness/useUniqueElementIds: static element*/}
								<Switch
									id="enabled"
									checked={enabled}
									onCheckedChange={(checked) =>
										setData("chaos.enabled", checked)
									}
									className="data-[state=checked]:bg-orange-500"
								/>
							</div>

							<Separator />

							{/* Latency Control */}
							<div className="space-y-4">
								<div className="flex items-center justify-between">
									<div>
										<Label className="text-base font-medium">
											Latency Injection
										</Label>
										<p className="text-sm text-muted-foreground">
											Add artificial delay to requests
										</p>
									</div>
									<Badge variant="outline" className="text-sm">
										{latency_ms}ms
									</Badge>
								</div>
								<Slider
									value={[latency_ms]}
									onValueChange={([value]) =>
										setData("chaos.latency_ms", value)
									}
									max={5000}
									step={100}
									disabled={!enabled}
									className="w-full"
								/>
								<div className="flex justify-between text-xs text-muted-foreground">
									<span>0ms</span>
									<span>2500ms</span>
									<span>5000ms</span>
								</div>
							</div>

							<Separator />

							{/* Failure Rate Control */}
							<div className="space-y-4">
								<div className="flex items-center justify-between">
									<div>
										<Label className="text-base font-medium">
											Failure Rate
										</Label>
										<p className="text-sm text-muted-foreground">
											Percentage of requests that fail
										</p>
									</div>
									<Badge variant="outline" className="text-sm">
										{failure_rate}%
									</Badge>
								</div>
								<Slider
									value={[failure_rate]}
									onValueChange={([value]) =>
										setData("chaos.failure_rate", value)
									}
									max={100}
									step={5}
									disabled={!enabled}
									className="w-full"
								/>
								<div className="flex justify-between text-xs text-muted-foreground">
									<span>0%</span>
									<span>50%</span>
									<span>100%</span>
								</div>
							</div>

							<Separator />

							{/* Failure Type Selector */}
							<div className="space-y-3">
								<div>
									<Label className="text-base font-medium">Failure Type</Label>
									<p className="text-sm text-muted-foreground">
										Type of error to simulate
									</p>
								</div>
								<Select
									value={failure_type}
									onValueChange={(value) =>
										setData("chaos.failure_type", value)
									}
									disabled={!enabled}
								>
									<SelectTrigger className="w-full">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{chaos.failure_types.map((type) => (
											<SelectItem key={type} value={type}>
												{formatFailureType(type)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</CardContent>
					</Card>

					{/* Action Buttons */}
					<div className="flex justify-end gap-3">
						<Button
							variant="outline"
							size="lg"
							onClick={() => window.location.reload()}
							disabled={processing}
						>
							Cancel
						</Button>
						<Button
							size="lg"
							onClick={handleSave}
							disabled={processing}
							className="min-w-32"
						>
							{processing
								? "Saving..."
								: recentlySuccessful
									? "Saved!"
									: "Save Configuration"}
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}
